import React, { useState, createContext, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../../config/axios.config';

export const MaskingContext = createContext({});

// Auto-lock timeout in milliseconds (10 minutes)
const AUTO_LOCK_TIMEOUT = 10 * 60 * 1000;

export const MaskingProvider = ({ children }) => {
    // Core state
    const [isMasked, setIsMasked] = useState(true); // Data is masked by default
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifiedAt, setVerifiedAt] = useState(null);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);

    // PIN-specific state
    const [hasPin, setHasPin] = useState(null); // null = loading, true = has PIN, false = no PIN
    const [showPinSetupDialog, setShowPinSetupDialog] = useState(false);
    const [isSettingPin, setIsSettingPin] = useState(false);
    const [showChangePinDialog, setShowChangePinDialog] = useState(false);
    const [showForgotPinDialog, setShowForgotPinDialog] = useState(false);
    const [isResettingPin, setIsResettingPin] = useState(false);

    // Auto-lock timer ref
    const autoLockTimerRef = useRef(null);
    const activityTimerRef = useRef(null);

    /**
     * Reset the auto-lock timer on user activity
     */
    const resetAutoLockTimer = useCallback(() => {
        if (autoLockTimerRef.current) {
            clearTimeout(autoLockTimerRef.current);
        }

        // Only set timer if data is currently unmasked
        if (!isMasked && verifiedAt) {
            autoLockTimerRef.current = setTimeout(() => {
                // Auto-lock: mask the data
                setIsMasked(true);
                setVerifiedAt(null);
                console.log('Auto-locked due to inactivity');
            }, AUTO_LOCK_TIMEOUT);
        }
    }, [isMasked, verifiedAt]);

    /**
     * Set up activity listeners for auto-lock
     */
    useEffect(() => {
        const handleActivity = () => {
            // Debounce activity detection
            if (activityTimerRef.current) {
                clearTimeout(activityTimerRef.current);
            }
            activityTimerRef.current = setTimeout(() => {
                resetAutoLockTimer();
            }, 1000);
        };

        // Listen for user activity
        const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            if (autoLockTimerRef.current) {
                clearTimeout(autoLockTimerRef.current);
            }
            if (activityTimerRef.current) {
                clearTimeout(activityTimerRef.current);
            }
        };
    }, [resetAutoLockTimer]);

    /**
     * Start auto-lock timer when data is unmasked
     */
    useEffect(() => {
        if (!isMasked && verifiedAt) {
            resetAutoLockTimer();
        }
        return () => {
            if (autoLockTimerRef.current) {
                clearTimeout(autoLockTimerRef.current);
            }
        };
    }, [isMasked, verifiedAt, resetAutoLockTimer]);

    /**
     * Check if user has PIN set on mount
     */
    useEffect(() => {
        const checkPinStatus = async () => {
            try {
                const response = await axiosInstance.get('/user/masking-pin/status');
                if (response.data.success) {
                    setHasPin(response.data.has_pin);
                }
            } catch (error) {
                console.error('Failed to check PIN status:', error);
                // Default to false if check fails
                setHasPin(false);
            }
        };

        // Only check if user is authenticated
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            checkPinStatus();
        }
    }, []);

    /**
     * Verify password with backend
     * @param {string} password - User's password
     * @returns {Promise<{success: boolean, message: string}>}
     */
    const verifyPassword = useCallback(async (password) => {
        setIsVerifying(true);
        try {
            const response = await axiosInstance.post('/user/verify-password', { password });

            if (response.data.success) {
                setIsMasked(false);
                setVerifiedAt(response.data.verified_at || new Date().toISOString());
                setShowPasswordDialog(false);
                return { success: true, message: 'Password verified successfully' };
            } else {
                return { success: false, message: response.data.message || 'Verification failed' };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to verify password';
            return { success: false, message };
        } finally {
            setIsVerifying(false);
        }
    }, []);

    /**
     * Verify PIN with backend
     * @param {string} pin - User's 4-digit PIN
     * @returns {Promise<{success: boolean, message: string, pinNotSet?: boolean}>}
     */
    const verifyPin = useCallback(async (pin) => {
        setIsVerifying(true);
        try {
            const response = await axiosInstance.post('/user/verify-masking-pin', { pin });

            if (response.data.success) {
                setIsMasked(false);
                setVerifiedAt(response.data.verified_at || new Date().toISOString());
                setShowPasswordDialog(false);
                return { success: true, message: 'PIN verified successfully' };
            } else {
                return { success: false, message: response.data.message || 'Verification failed' };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to verify PIN';
            const pinNotSet = error.response?.data?.pin_not_set || false;

            // If PIN is not set, prompt user to set one
            if (pinNotSet) {
                setHasPin(false);
                setShowPasswordDialog(false);
                setShowPinSetupDialog(true);
                return { success: false, message: 'Please set up your security PIN first', pinNotSet: true };
            }

            return { success: false, message };
        } finally {
            setIsVerifying(false);
        }
    }, []);

    /**
     * Set or update masking PIN
     * @param {string} pin - New 4-digit PIN
     * @param {string} pinConfirmation - PIN confirmation
     * @param {string} currentPin - Current PIN (required if updating)
     * @returns {Promise<{success: boolean, message: string}>}
     */
    const setMaskingPin = useCallback(async (pin, pinConfirmation, currentPin = null) => {
        setIsSettingPin(true);
        try {
            const payload = { pin, pin_confirmation: pinConfirmation };
            if (currentPin) {
                payload.current_pin = currentPin;
            }

            const response = await axiosInstance.post('/user/masking-pin', payload);

            if (response.data.success) {
                setHasPin(true);
                setShowPinSetupDialog(false);
                return { success: true, message: response.data.message };
            } else {
                return { success: false, message: response.data.message || 'Failed to set PIN' };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to set PIN';
            const errors = error.response?.data?.errors || {};
            return { success: false, message, errors };
        } finally {
            setIsSettingPin(false);
        }
    }, []);

    /**
     * Lock data (mask it)
     */
    const lockData = useCallback(() => {
        setIsMasked(true);
        setVerifiedAt(null);
        if (autoLockTimerRef.current) {
            clearTimeout(autoLockTimerRef.current);
        }
    }, []);

    /**
     * Request unlock - shows PIN dialog (or PIN setup if not set)
     */
    const requestUnlock = useCallback(() => {
        if (hasPin === false) {
            // User doesn't have a PIN set, show setup dialog
            setShowPinSetupDialog(true);
        } else {
            // Show PIN verification dialog
            setShowPasswordDialog(true);
        }
    }, [hasPin]);

    /**
     * Show PIN setup dialog
     */
    const showPinSetup = useCallback(() => {
        setShowPinSetupDialog(true);
    }, []);

    /**
     * Hide PIN setup dialog
     */
    const hidePinSetupDialog = useCallback(() => {
        setShowPinSetupDialog(false);
    }, []);

    /**
     * Show Change PIN dialog
     */
    const showChangePin = useCallback(() => {
        setShowChangePinDialog(true);
    }, []);

    /**
     * Hide Change PIN dialog
     */
    const hideChangePinDialog = useCallback(() => {
        setShowChangePinDialog(false);
    }, []);

    /**
     * Show Forgot PIN dialog
     */
    const showForgotPin = useCallback(() => {
        setShowPasswordDialog(false); // Close the PIN verification dialog
        setShowForgotPinDialog(true);
    }, []);

    /**
     * Hide Forgot PIN dialog
     */
    const hideForgotPinDialog = useCallback(() => {
        setShowForgotPinDialog(false);
    }, []);

    /**
     * Reset PIN using login password (Forgot PIN flow)
     * @param {string} password - User's login password
     * @param {string} newPin - New 4-digit PIN (optional)
     * @param {string} newPinConfirmation - PIN confirmation (required if newPin provided)
     * @returns {Promise<{success: boolean, message: string, oauthUser?: boolean}>}
     */
    const resetPinWithPassword = useCallback(async (password, newPin = null, newPinConfirmation = null) => {
        setIsResettingPin(true);
        try {
            const payload = { password };
            if (newPin) {
                payload.new_pin = newPin;
                payload.new_pin_confirmation = newPinConfirmation;
            }

            const response = await axiosInstance.post('/user/masking-pin/reset', payload);

            if (response.data.success) {
                // If new PIN was set, update hasPin state
                if (response.data.new_pin_set) {
                    setHasPin(true);
                } else {
                    // PIN was cleared, user needs to set new one
                    setHasPin(false);
                }
                setShowForgotPinDialog(false);
                return { success: true, message: response.data.message };
            } else {
                return { success: false, message: response.data.message || 'Failed to reset PIN' };
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to reset PIN';
            const oauthUser = error.response?.data?.oauth_user || false;
            return { success: false, message, oauthUser };
        } finally {
            setIsResettingPin(false);
        }
    }, []);

    /**
     * Toggle masking state
     * If currently masked, show password dialog
     * If currently unmasked, lock immediately
     */
    const toggleMasking = useCallback(() => {
        if (isMasked) {
            requestUnlock();
        } else {
            lockData();
        }
    }, [isMasked, requestUnlock, lockData]);

    /**
     * Hide password dialog
     */
    const hidePasswordDialog = useCallback(() => {
        setShowPasswordDialog(false);
    }, []);

    /**
     * Mask a PAN number
     * @param {string} pan - PAN number (e.g., "ABCDE1234F")
     * @returns {string} - Masked PAN (e.g., "XXXXX1234X")
     */
    const maskPAN = useCallback((pan) => {
        if (!pan || !isMasked) return pan;
        if (pan.length < 4) return 'XXXXXXXXXX';
        return 'XXXXX' + pan.slice(-5, -1) + 'X';
    }, [isMasked]);

    /**
     * Mask an Aadhaar number
     * @param {string} aadhaar - Aadhaar number (e.g., "123456789012")
     * @returns {string} - Masked Aadhaar (e.g., "XXXX-XXXX-9012")
     */
    const maskAadhaar = useCallback((aadhaar) => {
        if (!aadhaar || !isMasked) return aadhaar;
        const cleaned = aadhaar.replace(/\D/g, '');
        if (cleaned.length < 4) return 'XXXX-XXXX-XXXX';
        return 'XXXX-XXXX-' + cleaned.slice(-4);
    }, [isMasked]);

    /**
     * Mask an email address
     * @param {string} email - Email address (e.g., "john.doe@example.com")
     * @returns {string} - Masked email (e.g., "j***e@example.com")
     */
    const maskEmail = useCallback((email) => {
        if (!email || !isMasked) return email;
        const parts = email.split('@');
        if (parts.length !== 2) return '***@***.***';
        const name = parts[0];
        const domain = parts[1];
        if (name.length <= 2) {
            return name[0] + '***@' + domain;
        }
        return name[0] + '***' + name[name.length - 1] + '@' + domain;
    }, [isMasked]);

    /**
     * Mask a phone number
     * @param {string} phone - Phone number (e.g., "+91 98765 43210")
     * @returns {string} - Masked phone (e.g., "+91 XXXXX X3210")
     */
    const maskPhone = useCallback((phone) => {
        if (!phone || !isMasked) return phone;
        const cleaned = String(phone).replace(/\D/g, '');
        if (cleaned.length < 4) return 'XXXXXXXXXX';

        // Keep country code if present and last 4 digits
        if (cleaned.length >= 12) {
            // Has country code (91 + 10 digits)
            return '+91 XXXXX X' + cleaned.slice(-4);
        } else if (cleaned.length === 10) {
            return 'XXXXX X' + cleaned.slice(-4);
        }
        return 'XXXXX X' + cleaned.slice(-4);
    }, [isMasked]);

    /**
     * Mask a bank account number
     * @param {string} account - Account number
     * @returns {string} - Masked account (e.g., "XXXXXXXX1234")
     */
    const maskBankAccount = useCallback((account) => {
        if (!account || !isMasked) return account;
        const cleaned = account.replace(/\D/g, '');
        if (cleaned.length < 4) return 'XXXXXXXXXXXX';
        return 'X'.repeat(cleaned.length - 4) + cleaned.slice(-4);
    }, [isMasked]);

    /**
     * Mask a name (show first and last initial only)
     * @param {string} name - Full name (e.g., "John Doe")
     * @returns {string} - Masked name (e.g., "J*** D***")
     */
    const maskName = useCallback((name) => {
        if (!name || !isMasked) return name;
        const parts = name.trim().split(/\s+/);
        return parts.map(part => {
            if (part.length <= 1) return part;
            return part[0] + '***';
        }).join(' ');
    }, [isMasked]);

    /**
     * Mask a date of birth
     * @param {string} dob - Date string (e.g., "1990-05-15" or "15/05/1990")
     * @returns {string} - Masked DOB (e.g., "XX/XX/1990")
     */
    const maskDOB = useCallback((dob) => {
        if (!dob || !isMasked) return dob;
        // Try to extract year from various formats
        const str = String(dob);
        // ISO format: 1990-05-15
        const isoMatch = str.match(/(\d{4})-\d{2}-\d{2}/);
        if (isoMatch) return 'XX/XX/' + isoMatch[1];
        // DD/MM/YYYY or DD-MM-YYYY
        const dmyMatch = str.match(/\d{2}[\/\-]\d{2}[\/\-](\d{4})/);
        if (dmyMatch) return 'XX/XX/' + dmyMatch[1];
        // Just show masked
        return 'XX/XX/XXXX';
    }, [isMasked]);

    /**
     * Mask an address
     * @param {string} address - Full address string
     * @returns {string} - Masked address showing only city/state hint
     */
    const maskAddress = useCallback((address) => {
        if (!address || !isMasked) return address;
        const str = String(address).trim();
        if (str.length <= 5) return 'X'.repeat(str.length);
        // Show last 6 characters (usually city/pin) and mask the rest
        const visibleEnd = Math.min(8, Math.floor(str.length * 0.3));
        return 'X'.repeat(str.length - visibleEnd) + str.slice(-visibleEnd);
    }, [isMasked]);

    /**
     * Generic mask function - masks middle portion of any string
     * @param {string} value - Any string value
     * @param {number} showStart - Number of characters to show at start
     * @param {number} showEnd - Number of characters to show at end
     * @returns {string} - Masked value
     */
    const maskGeneric = useCallback((value, showStart = 2, showEnd = 2) => {
        if (!value || !isMasked) return value;
        if (value.length <= showStart + showEnd) return 'X'.repeat(value.length);
        const start = value.slice(0, showStart);
        const end = value.slice(-showEnd);
        const middle = 'X'.repeat(value.length - showStart - showEnd);
        return start + middle + end;
    }, [isMasked]);

    const value = {
        // State
        isMasked,
        isVerifying,
        verifiedAt,
        showPasswordDialog,

        // PIN State
        hasPin,
        showPinSetupDialog,
        isSettingPin,
        showChangePinDialog,
        showForgotPinDialog,
        isResettingPin,

        // Actions
        verifyPassword,
        verifyPin,
        setMaskingPin,
        lockData,
        requestUnlock,
        toggleMasking,
        hidePasswordDialog,
        showPinSetup,
        hidePinSetupDialog,

        // Change PIN actions
        showChangePin,
        hideChangePinDialog,

        // Forgot PIN actions
        showForgotPin,
        hideForgotPinDialog,
        resetPinWithPassword,

        // Masking utilities
        maskPAN,
        maskAadhaar,
        maskEmail,
        maskPhone,
        maskBankAccount,
        maskName,
        maskDOB,
        maskAddress,
        maskGeneric
    };

    return <MaskingContext.Provider value={value}>{children}</MaskingContext.Provider>;
};

/**
 * Custom hook to use masking context
 */
export const useMasking = () => {
    const context = React.useContext(MaskingContext);
    if (!context) {
        throw new Error('useMasking must be used within a MaskingProvider');
    }
    return context;
};

export default MaskingContext;
